import { CheckCircle, Circle, Lightbulb, Warning, ShieldCheck, Target, ListChecks, Users } from "@phosphor-icons/react";

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-white border border-slate-200 rounded-sm">
    <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
      <Icon size={18} weight="duotone" className="text-blue-700" />
      <h3 className="text-sm font-semibold tracking-wide uppercase text-slate-700">{title}</h3>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const ReadField = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold tracking-[0.08em] uppercase text-slate-400 mb-1">{label}</p>
    <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{value || <span className="text-slate-300">—</span>}</p>
  </div>
);

const Gate = ({ ok, label }) => (
  <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-sm border ${ok ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
    {ok ? <CheckCircle size={18} weight="fill" className="text-emerald-600" /> : <Circle size={18} className="text-slate-400" />}
    <span className="text-sm font-medium text-slate-700">{label}</span>
    <span className={`font-mono text-xs font-semibold ${ok ? "text-emerald-700" : "text-slate-500"}`}>{ok ? "YES" : "NO"}</span>
  </div>
);

export default function FocusArmorBlueprint({ doc }) {
  const f = doc.payload?.focus || {};
  const arm = doc.payload?.armor || {};
  const gk = f.gatekeepers || {};
  const progress = doc.progress || { done: 0, total: 0, pct: 0 };

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold tracking-[0.08em] uppercase text-slate-500">Execution Progress</span>
          <span className="font-mono text-sm font-semibold text-slate-700">{progress.done}/{progress.total} · {progress.pct}%</span>
        </div>
        <div className="h-3 bg-slate-100 rounded-sm overflow-hidden">
          <div className={`h-full ${progress.pct === 100 ? "bg-emerald-500" : "bg-blue-600"}`} style={{ width: `${progress.pct}%` }} />
        </div>
      </div>

      <div className="mt-6">
        <h2 className="text-xs font-bold tracking-[0.12em] uppercase text-blue-700 mb-3">Phase 1 · FOCUS — Strategic Decisions</h2>
        <div className="grid grid-cols-1 gap-4">
          <Section title="Context & Objectives" icon={Target}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <ReadField label="Fact-Find & Context" value={f.factFind} />
              <ReadField label="Strategic Intent" value={f.strategicIntent} />
              <ReadField label="Operational Objectives" value={f.operationalObjectives} />
              <ReadField label="Selected Course of Action" value={f.selectedOption} />
            </div>
          </Section>
          {(f.coas || []).some((c) => c) && (
            <Section title="Options & Risk Evaluation" icon={Warning}>
              <div className="space-y-3">
                {(f.coas || []).map((c, i) => c ? (
                  <div key={i} className="border border-slate-200 rounded-sm overflow-hidden">
                    <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                      <span className="font-mono text-xs font-semibold text-slate-600">COA {i + 1}</span>
                      <span className="ml-2 text-sm text-slate-700">{c}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200 text-sm">
                      <div className="p-3 flex gap-2"><Lightbulb size={16} className="text-emerald-600 shrink-0 mt-0.5" /><span className="text-slate-700 whitespace-pre-wrap">{f.risks?.[i]?.opportunity || "—"}</span></div>
                      <div className="p-3 flex gap-2"><Warning size={16} className="text-red-600 shrink-0 mt-0.5" /><span className="text-slate-700 whitespace-pre-wrap">{f.risks?.[i]?.risk || "—"}</span></div>
                    </div>
                  </div>
                ) : null)}
              </div>
            </Section>
          )}
          <Section title="Decision Gatekeepers" icon={ShieldCheck}>
            <div className="flex flex-wrap gap-3">
              <Gate ok={gk.valueAlignment} label="Value Alignment" />
              <Gate ok={gk.objectiveCertainty} label="Objective Certainty" />
              <div className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-slate-200 bg-slate-50">
                <span className="text-sm font-medium text-slate-700">Authority:</span>
                <span className="font-mono text-xs font-semibold text-slate-900">{gk.authorityBaseline || "—"}</span>
              </div>
            </div>
          </Section>
        </div>
      </div>

      <div className="mt-8">
        <h2 className="text-xs font-bold tracking-[0.12em] uppercase text-amber-700 mb-3">Phase 2 · ARMOR — Execution Checklist</h2>
        {(arm.alignment || arm.phases) && (
          <Section title="Alignment & Method" icon={Target}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <ReadField label="Alignment & Objective" value={arm.alignment} />
              <ReadField label="High-Level Phases" value={arm.phases} />
            </div>
          </Section>
        )}
        <div className="space-y-4 mt-4">
          {(arm.workingGroups || []).length === 0 && <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-6 text-center">No working groups defined.</p>}
          {(arm.workingGroups || []).map((g) => {
            const total = g.tasks.length;
            const done = g.tasks.filter((t) => t.status).length;
            return (
              <div key={g.id} className="bg-white border border-slate-200 rounded-sm">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-2"><Users size={18} weight="duotone" className="text-slate-700" /><h3 className="text-sm font-semibold text-slate-900">{g.name || "Working Group"}</h3></div>
                  <span className="font-mono text-xs text-slate-500">{done}/{total}</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {g.tasks.map((t) => (
                    <div key={t.id} className="w-full flex items-center gap-3 px-5 py-3 text-left">
                      {t.status ? <CheckCircle size={22} weight="fill" className="text-emerald-500 shrink-0" /> : <Circle size={22} className="text-slate-300 shrink-0" />}
                      <span className={`text-sm ${t.status ? "text-slate-400 line-through" : "text-slate-800"}`}>{t.name || "Untitled task"}</span>
                    </div>
                  ))}
                  {g.tasks.length === 0 && <p className="px-5 py-3 text-sm text-slate-400">No tasks assigned.</p>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {(arm.workingGroups || []).length > 0 && (
            <Section title="Operational Boundaries" icon={ListChecks}>
              <div className="space-y-3">
                {(arm.workingGroups || []).map((g, gi) => (
                  <div key={g.id} className="text-sm">
                    <p className="font-mono text-xs font-semibold text-slate-600">{g.name || `Group ${gi + 1}`}</p>
                    <p className="text-emerald-700 mt-0.5"><span className="font-medium">Freedoms:</span> {arm.boundaries?.[gi]?.freedoms || "—"}</p>
                    <p className="text-red-700 mt-0.5"><span className="font-medium">Constraints:</span> {arm.boundaries?.[gi]?.constraints || "—"}</p>
                  </div>
                ))}
              </div>
            </Section>
          )}
          <Section title="Milestones & Resources" icon={Target}>
            <div className="space-y-3">
              <ReadField label="Hard Deadlines" value={arm.milestones?.deadlines} />
              <ReadField label="Key Milestones" value={arm.milestones?.milestones} />
              <ReadField label="Review Cadence" value={arm.milestones?.reviewCadence} />
              <ReadField label="Internal Resources" value={arm.resources?.internal} />
              <ReadField label="External SMEs" value={arm.resources?.externalSMEs} />
              <ReadField label="Tracked KPIs" value={arm.resources?.kpis} />
            </div>
          </Section>
        </div>
      </div>
    </>
  );
}
